import './MultipleEditBufferCell.scss';

import { IconEdit } from '@hulu-react-style-components/icons';
import React, { memo } from 'react';
import { useToggle } from 'react-use';

import BufferEditInput from '../../../../../../../../../../pages/Trafficking/TraffickingPage/cells/BufferCell/BufferEditInput';
import BufferLabel from '../../../../../../../../../../pages/Trafficking/TraffickingPage/cells/BufferCell/BufferLabel';
import bem from '../../../../../../../../../../utils/bem';
import { formatPercentage } from '../../../../../../../../../../utils/formatting';
import { AUTOMATED_TOOLTIP, USER_APPLIED_TOOLTIP } from '../constants';

type Props = Readonly<{
  automatedDeliveryBufferPercent: number | null | undefined;
  deliveryBufferPercent: number | null | undefined;
  id: string;
  isToggleOn: boolean;
  onSubmitBuffer: (value: number, entityId: string) => void;
  hideAutomated?: boolean;
  isSOV?: boolean;
}>;

const [block, element] = bem('multiple-edit-buffer-cell');

function formatBufferValue(value: number | null | undefined): string {
  if (value === null || value === undefined) return formatPercentage(0);
  return formatPercentage(value);
}

function MultipleEditBufferCell({
  automatedDeliveryBufferPercent,
  deliveryBufferPercent,
  id,
  isToggleOn,
  onSubmitBuffer,
  hideAutomated = false,
  isSOV = false,
}: Props): React.JSX.Element {
  const [active, toggleEdit] = useToggle(false);

  const isDisabled = isToggleOn || isSOV;
  const userAppliedDisplay = formatBufferValue(deliveryBufferPercent);

  const handleEdit = (): void => {
    if (isDisabled) return;
    toggleEdit(true);
  };

  const handleEditSubmit = (value: number): void => {
    onSubmitBuffer(value, id);
    toggleEdit(false);
  };

  const handleEditCancel = (): void => {
    toggleEdit(false);
  };

  const renderUserApplied = (): React.JSX.Element => {
    if (active) {
      return (
        <BufferEditInput
          initialValue={deliveryBufferPercent?.toString() ?? '0'}
          onSubmit={handleEditSubmit}
          onCancel={handleEditCancel}
        />
      );
    }

    return (
      <button
        className={element('percent', isDisabled ? 'disabled' : '')}
        onClick={handleEdit}
        disabled={isDisabled}
        data-testid="buffer-user-applied"
      >
        <span className={element('percent-value', isDisabled ? 'disabled' : '')}>{userAppliedDisplay}</span>
        {!isDisabled ? <IconEdit className={element('percent-icon')} /> : <span className={element('icon-spacer')} />}
      </button>
    );
  };

  return (
    <div className={block()} data-testid="multiple-edit-buffer-cell">
      {hideAutomated ? null : (
        <div className={element('row', isToggleOn ? 'active' : 'inactive')}>
          <BufferLabel
            label="Automated"
            tooltipId={`${id}_automated_info`}
            tooltipMessage={AUTOMATED_TOOLTIP}
            showTooltip={true}
          />
          <span className={element('percent')}>
            <span className={element('percent-value', isToggleOn ? '' : 'disabled')}>
              {formatBufferValue(automatedDeliveryBufferPercent)}
            </span>
            <span className={element('icon-spacer')} />
          </span>
        </div>
      )}
      <div className={element('row', isToggleOn || isSOV ? 'inactive' : 'active')}>
        <BufferLabel
          label="User-Applied"
          tooltipId={`${id}_user_applied_info`}
          tooltipMessage={isToggleOn ? AUTOMATED_TOOLTIP : USER_APPLIED_TOOLTIP}
          showTooltip={true}
        />
        {renderUserApplied()}
      </div>
    </div>
  );
}

export default memo(MultipleEditBufferCell);
